import type { ComponentChildren } from "preact";
import { IcoX } from "../icons";
import { useFocusTrap } from "./use-focus-trap";

type SlideOverProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ComponentChildren;
  footer?: ComponentChildren;
};

export function SlideOver({
  open,
  onClose,
  title,
  children,
  footer,
}: SlideOverProps) {
  const panelRef = useFocusTrap<HTMLDivElement>({
    open,
    onEscape: onClose,
  });

  if (!open) return null;

  return (
    <div
      class="fixed inset-0 z-[60] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="slideover-title"
    >
      {/* Backdrop */}
      <div
        class="absolute inset-0 bg-black/30 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        class="relative w-full max-w-lg bg-surface shadow-xl flex flex-col animate-slide-in-right"
      >
        {/* Header */}
        <div class="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 id="slideover-title" class="text-lg font-semibold text-text">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            class="p-2 -m-2 text-text-muted hover:text-text transition-colors"
          >
            <IcoX class="size-5" />
          </button>
        </div>

        {/* Content */}
        <div class="flex-1 overflow-y-auto p-6">{children}</div>

        {/* Footer */}
        {footer && (
          <div class="flex-shrink-0 px-6 py-4 border-t border-border bg-surface">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
